import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { SHAPES } from './shapes';
import COUNTRIES from './countries.json';

const TABS = [
  { key: 'shapes', label: 'Shapes' },
  { key: 'lines', label: 'Lines' },
  { key: 'flags', label: 'Flags' },
];

const LINE_PRESETS = [
  { label: 'Thin', extra: { strokeWidth: 2 } },
  { label: 'Regular', extra: { strokeWidth: 4 } },
  { label: 'Thick', extra: { strokeWidth: 10, cap: 'butt' } },
  { label: 'Dashed', extra: { strokeWidth: 3, dash: 'dashed' } },
  { label: 'Dotted', extra: { strokeWidth: 4, dash: 'dotted' } },
  { label: 'Vertical', extra: { strokeWidth: 3, rotation: 90, w: 240, name: 'Vertical line' } },
  { label: 'Diagonal', extra: { strokeWidth: 3, rotation: -45, w: 240, name: 'Diagonal line' } },
  { label: 'Divider', extra: { strokeWidth: 1, stroke: '#ffffff', name: 'Divider' } },
];

const LinePreview = ({ extra }) => {
  const sw = Math.min(extra.strokeWidth || 3, 6);
  const dash = extra.dash === 'dashed' ? `${sw * 3} ${sw * 2}` : extra.dash === 'dotted' ? `0.1 ${sw * 2}` : undefined;
  return (
    <svg viewBox="0 0 100 40" className="h-8 w-full">
      <line
        x1="8" y1="20" x2="92" y2="20"
        stroke="#EAB308"
        strokeWidth={sw}
        strokeLinecap={extra.dash === 'dotted' || extra.cap !== 'butt' ? 'round' : 'butt'}
        strokeDasharray={dash}
        transform={extra.rotation ? `rotate(${extra.rotation} 50 20)` : undefined}
      />
    </svg>
  );
};

export const ShapePicker = ({ onAdd, onClose, className = '' }) => {
  const [tab, setTab] = useState('shapes');
  const [q, setQ] = useState('');

  const flags = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(term) || c.code.includes(term)) : COUNTRIES;
  }, [q]);

  const tile = 'tap-none flex flex-col items-center justify-center gap-1 rounded-xl border border-white/[0.06] bg-white/[0.03] p-2 text-[10px] text-[#a1a1aa] transition-colors hover:border-[#A855F7] hover:text-white active:scale-95';

  return (
    <div className={`flex flex-col bg-[#0f0f11] ${className}`} data-testid="shape-picker">
      <div className="flex items-center gap-1 border-b border-white/[0.06] px-2 py-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`shape-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`h-8 flex-1 rounded-full text-xs font-semibold transition-colors ${tab === t.key ? 'bg-[#A855F7] text-white' : 'text-[#a1a1aa] hover:text-white'}`}
          >
            {t.label}
          </button>
        ))}
        <button type="button" onClick={onClose} aria-label="Close" className="ml-1 flex h-8 w-8 items-center justify-center rounded-full text-[#71717a] hover:text-white" data-testid="shape-picker-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {tab === 'shapes' && (
          <div className="grid grid-cols-4 gap-2">
            <button type="button" className={tile} data-testid="shape-rect" onClick={() => onAdd('rect', { radius: 0, name: 'Rectangle' })}>
              <svg viewBox="0 0 100 100" className="h-9 w-9"><rect x="5" y="15" width="90" height="70" fill="#A855F7" /></svg>Rectangle
            </button>
            <button type="button" className={tile} data-testid="shape-rounded" onClick={() => onAdd('rect', { radius: 24, name: 'Rounded box' })}>
              <svg viewBox="0 0 100 100" className="h-9 w-9"><rect x="5" y="15" width="90" height="70" rx="18" fill="#A855F7" /></svg>Rounded
            </button>
            <button type="button" className={tile} data-testid="shape-circle" onClick={() => onAdd('ellipse', { name: 'Circle' })}>
              <svg viewBox="0 0 100 100" className="h-9 w-9"><circle cx="50" cy="50" r="45" fill="#A855F7" /></svg>Circle
            </button>
            <button type="button" className={tile} data-testid="shape-oval" onClick={() => onAdd('ellipse', { w: 200, h: 120, name: 'Oval' })}>
              <svg viewBox="0 0 100 100" className="h-9 w-9"><ellipse cx="50" cy="50" rx="47" ry="30" fill="#A855F7" /></svg>Oval
            </button>
            {Object.entries(SHAPES).map(([key, def]) => (
              <button key={key} type="button" className={tile} data-testid={`shape-${key}`} onClick={() => onAdd('shape', { shape: key, name: def.label })}>
                <svg viewBox="-4 -4 108 108" className="h-9 w-9"><path d={def.path} fill="#A855F7" fillRule={def.evenOdd ? 'evenodd' : 'nonzero'} /></svg>
                <span className="truncate max-w-full">{def.label}</span>
              </button>
            ))}
          </div>
        )}

        {tab === 'lines' && (
          <div className="grid grid-cols-2 gap-2">
            {LINE_PRESETS.map((p) => (
              <button key={p.label} type="button" className={`${tile} py-3`} data-testid={`line-${p.label.toLowerCase()}`} onClick={() => onAdd('line', { name: `${p.label} line`, ...p.extra })}>
                <LinePreview extra={p.extra} />
                {p.label}
              </button>
            ))}
          </div>
        )}

        {tab === 'flags' && (
          <>
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#52525b]" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search countries"
                data-testid="flag-search-input"
                className="h-9 w-full rounded-lg border border-white/[0.08] bg-[#09090b] pl-8 pr-2 text-sm text-white placeholder:text-[#52525b] focus:border-[#A855F7] focus:outline-none"
              />
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="flag-grid">
              {flags.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  title={c.name}
                  data-testid={`flag-${c.code}`}
                  className={tile}
                  onClick={() => onAdd('image', { src: `/flags/${c.code}.svg`, fit: 'fill', w: 160, h: 120, radius: 6, name: `${c.name} flag` })}
                >
                  <img src={`/flags/${c.code}.svg`} alt={c.name} loading="lazy" className="h-7 w-10 rounded-sm object-fill shadow" />
                  <span className="w-full truncate text-center">{c.name}</span>
                </button>
              ))}
              {flags.length === 0 && <p className="col-span-4 py-6 text-center text-xs text-[#52525b]">No countries match.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
